import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import externalPageApi from "api/externalPage";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useNotify from "hooks/useNotify";
import { DELIVERED_STATUS } from "utils/constantValues";

const useInviteAssignees = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { notifyError } = useNotify();
  const { token, slug: slugParam } = useParams();

  const emailParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("email");
    return value ? decodeURIComponent(value) : "";
  }, [location.search]);

  const slug = slugParam || "invitation";

  const [isLoading, setIsLoading] = useState({
    accept: false,
    reject: false,
    statusCheck: false,
  });
  const [successMsg, setSuccessMsg] = useState("");
  const [isAccept, setIsAccept] = useState();
  const [isDeliveredStatus, setIsDeliveredStatus] = useState(false);

  const [poDetailsLoading, setPoDetailsLoading] = useState(false);
  const [poDetails, setPoDetails] = useState(null);
  const [isZeroTotal, setIsZeroTotal] = useState(false);

  const goToStep = useCallback(
    (nextSlug, { email } = {}) => {
      const finalEmail = email ?? emailParam;
      const query = finalEmail
        ? `?email=${encodeURIComponent(finalEmail)}`
        : "";
      navigate(`/order/invitePendingAssignees/${token}/${nextSlug}${query}`);
    },
    [token, emailParam]
  );

  const goToPoDetails = useCallback(
    (email) => goToStep("poDetails", { email }),
    [goToStep]
  );

  const fetchPoDetails = useCallback(async () => {
    setPoDetailsLoading(true);
    try {
      const res = await externalPageApi.getPoDetails(token, emailParam);
      const poData = res?.data?.data
      const isZeroTotalPo = Number(poData?.totalWithVat) === 0;
      setIsZeroTotal(isZeroTotalPo);
      setPoDetails(poData || null);
      setIsAccept(true);
      if (isZeroTotalPo) {
        setSuccessMsg(t("inviteAssigneeAcceptMsg"));
      }
    } catch (err) {
      console.error(err);
      notifyError(
        err?.response?.data?.message ?? err?.message ?? "Something went wrong!"
      );
    } finally {
      setPoDetailsLoading(false);
    }
  }, [token, emailParam]);

  const onInviteAssignee = async (data, accept) => {
    try {
      setIsAccept(accept);
      setIsLoading(
        accept
          ? { accept: true, reject: false, statusCheck: true }
          : { accept: false, reject: true, statusCheck: true }
      );

      const payload = {
        isAcceptInvitation: accept,
        token,
        email: emailParam || data.email,
      };

      const res = await externalPageApi.respondPendingAssignee(payload);
      console.log('isAutomated ->>> ', res.data)
      const isAutomated =
        res?.data?.isAutomated || res?.data?.data?.isAutomated;

      if (accept && isAutomated) {
        goToPoDetails(payload.email);
        return;
      }

      setSuccessMsg(
        accept ? t("inviteAssigneeAcceptMsg") : t("inviteAssigneeRejectMsg")
      );
    } catch (err) {
      console.error(err);
      notifyError(
        err?.response?.data?.message ?? err?.message ?? "Something went wrong!"
      );
    } finally {
      setIsLoading({ accept: false, reject: false, statusCheck: true });
    }
  };

  const onPoDecision = async (accept) => {
    try {
      setIsAccept(accept);
      setIsLoading(
        accept
          ? { accept: true, reject: false, statusCheck: true }
          : { accept: false, reject: true, statusCheck: true }
      );

      await externalPageApi.respondExposedPOForAllocation({
        token,
        email: emailParam,
        acceptance: Boolean(accept),
      });

      setSuccessMsg(accept ? t("poAcceptMsg") : t("rejectTask"));
    } catch (err) {
      console.error(err);
      notifyError(
        err?.response?.data?.message ?? err?.message ?? "Something went wrong!"
      );
    } finally {
      setIsLoading({ accept: false, reject: false, statusCheck: true });
    }
  };

  const orderStatusValidation = useCallback(async () => {
    try {
      const payload = { token };
      const orderStatus =
        await externalPageApi.orderStatusPendingAssigneeVAlidation(payload);
      if (orderStatus.data === DELIVERED_STATUS) {
        setIsLoading({ accept: false, reject: false, statusCheck: true });
        setIsDeliveredStatus(true);
        setSuccessMsg("orderIsInDeliveredStatus");
      }
      setIsLoading({ accept: false, reject: false, statusCheck: true });
    } catch (err) {
      console.error(err);
      notifyError(
        err?.response?.data?.message ?? err?.message ?? "Something went wrong!"
      );
    }
  }, [token, notifyError]);

  // Ensure correct step is loaded on refresh
  useEffect(() => {
    if (slug === "poDetails" && !poDetails) {
      fetchPoDetails();
    }
  }, [slug, poDetails, fetchPoDetails]);

  return {
    // navigation helpers
    navigate,
    goToStep,
    goToPoDetails,
    // route info
    slug,
    emailParam,
    // loading/status
    isLoading,
    successMsg,
    isAccept,
    isDeliveredStatus,
    // actions
    onInviteAssignee,
    onPoDecision,
    // po details state
    poDetailsLoading,
    poDetails,
    // external validations
    orderStatusValidation,
    isZeroTotal
  };
};

export default useInviteAssignees;
